import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { touchInteractiveSession } from "../lib/inactivity";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function SignUp() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const next = search.get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    // Native password managers may fill fields without updating React state.
    // FormData keeps registration and the immediately following login aligned
    // with the actual submitted password.
    const values = new FormData(e.currentTarget);
    const submittedPassword = String(values.get("password") || pw);
    const submittedConfirmation = String(values.get("confirm_password") || pw2);
    const submittedEmail = String(values.get("email") || email).trim().toLowerCase();
    const submittedName = String(values.get("name") || name).trim();
    if (submittedPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (submittedPassword !== submittedConfirmation) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/register", {
        email: submittedEmail,
        password: submittedPassword,
        confirm_password: submittedConfirmation,
        name: submittedName || undefined,
      });
      if (r.data?.session_token) localStorage.setItem("session_token", r.data.session_token);
      touchInteractiveSession();
      await refresh();
      toast.success(`Welcome, ${r.data.user.name}!`);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300">
          <ArrowLeft size={14}/> Back
        </Link>
        <div className="mt-6">
          <div className="label-eyebrow">Get started</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Create account</h1>
          <p className="text-sm text-zinc-400 mt-2">Real-time location sharing for the people you trust.</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signup-name"
              name="name" autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value.slice(0, 40))}
              placeholder="Your name (optional)"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signup-email"
              name="email" type="email" autoComplete="email" required
              autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signup-password"
              name="password" type={show ? "text" : "password"} autoComplete="new-password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Password (min 8 characters)"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              {show ? <EyeOff size={14}/> : <Eye size={14}/>}
            </button>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signup-confirm"
              name="confirm_password" type={show ? "text" : "password"} autoComplete="new-password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>
          <button
            data-testid="signup-submit"
            type="submit" disabled={busy}
            className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-zinc-400">
          Already have an account?{" "}
          <Link
            to={`/signin${next !== "/dashboard" ? `?next=${encodeURIComponent(next)}` : ""}`}
            data-testid="goto-signin"
            className="text-[#007AFF] font-bold"
          >
            Sign in →
          </Link>
        </div>

        <div className="mt-6 text-[11px] text-zinc-600 text-center">
          By creating an account you agree to share your location only with people you accept.
        </div>
      </div>
    </div>
  );
}
